import { Fragment, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { getModule } from "store/userSlice";
import { useOutletContext } from "react-router-dom";
import { fetchGroups, getGroups, getGroupsError, updateError } from "store/groupsSlice";
import { api } from "helpers/axios";
import { errorMessage, fileDownloadResponse } from "helpers/common";
import * as Sentry from "@sentry/react";
import { Tooltip as ReactTooltip } from "react-tooltip";
import "react-tooltip/dist/react-tooltip.css";
import GroupForm from "./GroupForm";
import { tableResize } from "helpers/tableResize";
import TextTruncate from "components/ui/TextTruncate";
import TableScrollbar from "components/EmployeeManagement/TableScrollbar";
import ErrorAlert from "components/ui/ErrorAlert";
import { Button, Modal } from "components/ui";
import { helpTextIcon } from "helpers/helpText";
import { useTranslation } from "translation/useTranslation";
import { isDbModule } from "helpers/module";

export default function Groups() {
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);

  const { editableTranslate, translate } = useTranslation();

  const currentModule = useSelector(getModule);

  const { helps } = useOutletContext();

  const isDbModuleActive = useMemo(() => {
    return isDbModule(currentModule);
  }, [currentModule]);

  const error = useSelector(getGroupsError);

  const groups = useSelector(getGroups);

  const [groupsData, setGroupsData] = useState(null);

  const [selectedGroup, setSelectedGroup] = useState(null);

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteData, setDeleteData] = useState({});

  const theadRef = useRef(null);

  const [exportLoading, setExportLoading] = useState(false);

  const handleExport = async (e = null, fileType) => {
    e?.preventDefault();

    setExportLoading(true);
    try {
      const response = await api.get('/groups/export', {
        params: { file_type: fileType },
        responseType: 'blob',
      });
      const ext = fileType === 'excel' ? 'xlsx' : 'csv';
      fileDownloadResponse(response, `groups.${ext}`);
    } catch (err) {
      dispatch(updateError(errorMessage(err, 'export groups')));
      Sentry.captureException(err);
    } finally {
      setExportLoading(false);
    }
  };

  const [sortCols, setSortCols] = useState([['category', 'asc']]);
  const [sortPinCol, setSortPinCol] = useState(null);

  const [resizedCount, setResizedCount] = useState(0);
  const [triggerColWidth, setTriggerColWidth] = useState(0);

  const tableRef = useRef(null);

  const objSortCols = useMemo(() => {
    const obj = {};

    for (const sc of sortCols) {
      obj[sc[0]] = sc[1];
    }

    return obj;
  }, [sortCols]);

  const categoryOptions = useMemo(() => {
    if (groups) {
      const arr = [];
      groups.filter(g => g.category).forEach(g => {
        const cat = g.category;
        if (!arr.includes(cat)) {
          arr.push(cat);
        }
      });
      arr.sort();

      return arr.map(a => ({ value: a, label: a }));
    }
    return [];
  }, [groups]);

  useEffect(() => {
    dispatch(fetchGroups());

    const theadEl = theadRef.current;
    if (!theadEl) return;

    const handleScrollTable = () => {
      const parentEl = theadEl.parentElement;
      const coordinates = parentEl.getBoundingClientRect();
      const navHeight = document.getElementById('navbar-menu')?.offsetHeight || 0;
      if (coordinates.y < navHeight) {
        theadEl.style.transform = `translate3d(0, ${-coordinates.y + navHeight}px, 0)`;
      } else {
        theadEl.style.transform = 'translate3d(0,0,0)';
      }
    };
    window.addEventListener('scroll', handleScrollTable);
    return () => window.removeEventListener('scroll', handleScrollTable);
  }, []);

  useEffect(() => {
    const table = tableRef.current;
    if (!table) return;

    tableResize(table, 'groups', true, {}, {}, setResizedCount);
  }, [groups, triggerColWidth]);

  useEffect(() => {
    if (groups) {
      const sortedGroups = [...groups];
      sortGroups(sortedGroups, sortCols);
      setGroupsData(sortedGroups);
      setLoading(false);
    } else {
      setGroupsData(null);
      setLoading(true);
    }
  }, [groups, sortCols]);

  useEffect(() => {
    if (groups && selectedGroup) {
      for (const group of groups) {
        if (group.hash_id === selectedGroup?.hash_id) {
          setSelectedGroup(group);
          break;
        }
      }
    } else {
      setSelectedGroup(null);
    }
  }, [groups]);

  const handleShowDelete = (group) => {
    setShowDeleteModal(true);
    setDeleteData(group);
  };

  const handleHideDelete = () => {
    setShowDeleteModal(false);
    setDeleteData({});
  };

  const handleYesDelete = async () => {
    dispatch(updateError(null));

    try {
      await api.delete(`/groups/${deleteData.hash_id}`);
      setSelectedGroup(null);
      handleHideDelete();
      dispatch(fetchGroups());
    } catch (err) {
      handleHideDelete();
      dispatch(updateError(errorMessage(err, 'delete group')));
      Sentry.captureException(err);
    }
  };

  const handleChangeSortCol = (field) => {
    const newSortCols = [...sortCols];
    let found = false;
    for (let i = 0; i < newSortCols.length; i++) {
      if (newSortCols[i][0] === field) {
        newSortCols[i][1] = (newSortCols[i][1] === 'asc' ? 'desc' : 'asc');
        found = true;
        break;
      }
    }
    if (!found) {
      if (newSortCols.length > 1 || !sortPinCol) newSortCols.pop(); // max 2, so if > 1 or no pin, we remove the last one first before push
      newSortCols.push([field, 'asc']);
    }
    setSortCols(newSortCols);
  };

  const handleSetSortPinCol = (field) => {
    if (sortPinCol === field) {
      setSortPinCol(null);
      setSortCols([sortCols[0]]);
    } else {
      setSortPinCol(field);
      setSortCols(sortCols.filter(sc => sc[0] === field));
    }
  };

  useEffect(() => {
    if (!sortPinCol) setTriggerColWidth(p => p + 1);
  }, [sortCols]);

  const sortGroups = (arr, sortCols) => {
    arr.sort((a, b) => {
      for (const [key, direction] of sortCols) {
        let left, right;

        switch (key) {
          case 'category':
          case 'name':
            left = (a[key] || '').toLowerCase();
            right = (b[key] || '').toLowerCase();
            break;
          case 'evaluator_reviewer':
            left = a.evaluators_count + a.reviewers_count;
            right = b.evaluators_count + b.reviewers_count;
            break;
          default:
            left = a?.name?.toLowerCase();
            right = b?.name?.toLowerCase();
        }

        const multiplier = direction === 'asc' ? 1 : -1;
        if (left < right) return -1 * multiplier;
        if (left > right) return 1 * multiplier;
      }
      return 0;
    });
  };

  const getLabel = (key, translation_key, fallback) => {
    const sKey = key.replaceAll('.', '-');
    const help = helps && helps.filter(h => h.key === sKey)[0] || null;

    return <>
      {editableTranslate(translation_key, fallback)}
      {help && helpTextIcon(sKey, help.short_text, 'text-normal')}
    </>
  };

  return (
    <>
      <div className="col-lg col-md-9 section-col-right d-flex">
        <div className={`bg-white w-100 p-4 content-card`}>
          <div className="d-flex justify-content-between">
            <h4 className="mb-0">{editableTranslate("global.menu.settings.groups", "Gruppen")}</h4>
            <div className="text-right mb-3 d-flex justify-content-end" style={{ gap: '0.5rem' }}>
              <div className="dropdown">
                <Button
                  variant="secondary"
                  className="ml-3 groups-export-btn dropdown-toggle"
                  data-toggle="dropdown"
                  loading={exportLoading} data-testid="download-button"
                >
                  {!exportLoading && <i className="bi bi-file-earmark-arrow-down mr-1"></i>}{' '}
                  {editableTranslate('group_setting.export_groups', 'Gruppennamen exportieren')}
                </Button>
                <div className="dropdown-menu dropdown-menu-right">
                  <a className="dropdown-item" href="#" onClick={(e) => handleExport(e, 'csv')} data-testid="export-csv-button">CSV</a>
                  <a className="dropdown-item" href="#" onClick={(e) => handleExport(e, 'excel')} data-testid="export-excel-button">Excel (.xlsx)</a>
                </div>
              </div>
              <Button onClick={() => setSelectedGroup(selectedGroup === '-' ? null : '-')} variant="secondary" data-testid="add-button">
                <i className="bi bi-plus"></i> {editableTranslate('group_setting.new_group', 'Gruppe anlegen')}
              </Button>
            </div>
          </div>

          {
            error && <div>
              <ErrorAlert error={error} onClose={() => dispatch(updateError(null))} />
            </div>
          }

          <div className="row">
            <div className={'col'}>
              <div className="table-responsive has-custom-scrollbar">
                <table ref={tableRef} className="table common-table" data-testid="groups-table">
                  <thead ref={theadRef} className="sticky-top">
                    <tr>
                      <th scope="col" className="text-center w-1px"></th>
                      <th scope="col" className="cursor-pointer">
                        <span className="float-right floating-icons">
                          <Button variant="ghost" size="inline" className="p-0 ml-1" icon={`bi-bold ${objSortCols['category'] ? (objSortCols['category'] === 'asc' ? 'bi-arrow-up' : 'bi-arrow-down') : 'bi-arrow-up v-hidden'}`} onClick={() => handleChangeSortCol('category')} data-testid="sort-category" />
                          {
                            objSortCols['category'] && <Button variant="ghost" size="inline" className="position-relative z-2 p-0 ml-1" icon={`bi-bold ${sortPinCol === 'category' ? 'bi-pin-angle-fill text-primary' : 'bi-pin-angle'}`} onClick={() => handleSetSortPinCol('category')} data-testid="sort-pin-category" />
                          }
                        </span>
                        <span data-testid="header-category" className="th-span" onClick={() => handleChangeSortCol('category')}>
                          {getLabel('category', 'group_setting.header.category', 'Category')}
                          <i className={`bi bi-bold bi-arrow-up invisible ml-1`}></i>
                          <i className={`bi bi-bold bi-pin-angle-fill invisible ml-1`}></i>
                        </span>
                        <div className="th-resize-div" data-field={'category'}></div>
                      </th>
                      <th scope="col" className="cursor-pointer">
                        <span className="float-right floating-icons">
                          <Button variant="ghost" size="inline" className="p-0 ml-1" icon={`bi-bold ${objSortCols['name'] ? (objSortCols['name'] === 'asc' ? 'bi-arrow-up' : 'bi-arrow-down') : 'bi-arrow-up v-hidden'}`} onClick={() => handleChangeSortCol('name')} data-testid="sort-name" />
                          {
                            objSortCols['name'] && <Button variant="ghost" size="inline" className="position-relative z-2 p-0 ml-1" icon={`bi-bold ${sortPinCol === 'name' ? 'bi-pin-angle-fill text-primary' : 'bi-pin-angle'}`} onClick={() => handleSetSortPinCol('name')} data-testid="sort-pin-name" />
                          }
                        </span>
                        <span data-testid="header-name" className="th-span" onClick={() => handleChangeSortCol('name')}>
                          {editableTranslate('group_setting.header.name')}
                          <i className={`bi bi-bold bi-arrow-up invisible ml-1`}></i>
                          <i className={`bi bi-bold bi-pin-angle-fill invisible ml-1`}></i>
                        </span>
                        <div className="th-resize-div" data-field={'name'}></div>
                      </th>
                      <th scope="col" className="cursor-pointer">
                        <span className="float-right floating-icons">
                          <Button variant="ghost" size="inline" className="p-0 ml-1" icon={`bi-bold ${objSortCols['evaluator_reviewer'] ? (objSortCols['evaluator_reviewer'] === 'asc' ? 'bi-arrow-up' : 'bi-arrow-down') : 'bi-arrow-up v-hidden'}`} onClick={() => handleChangeSortCol('evaluator_reviewer')} data-testid="sort-evaluator-reviewer" />
                          {
                            objSortCols['evaluator_reviewer'] && <Button variant="ghost" size="inline" className="position-relative z-2 p-0 ml-1" icon={`bi-bold ${sortPinCol === 'evaluator_reviewer' ? 'bi-pin-angle-fill text-primary' : 'bi-pin-angle'}`} onClick={() => handleSetSortPinCol('evaluator_reviewer')} data-testid="sort-pin-evaluator-reviewer" />
                          }
                        </span>
                        <span data-testid="header-evaluator-reviewer" className="th-span" onClick={() => handleChangeSortCol('evaluator_reviewer')}>
                          {
                            isDbModuleActive ? getLabel('first_second_evaluator', 'state_employee.first_second_evaluator', 'First/Second Evaluator') : <>
                              {getLabel('evaluator_reviewer', 'group_setting.header.evaluator_reviewer', 'Evaluator/Reviewer')}
                            </>
                          }
                          <i className={`bi bi-bold bi-arrow-up invisible ml-1`}></i>
                          <i className={`bi bi-bold bi-pin-angle-fill invisible ml-1`}></i>
                        </span>
                        <div className="th-resize-div" data-field={'evaluator_reviewer'}></div>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {
                      selectedGroup === '-' && <tr className="detail-row">
                        <td colSpan={4}>
                          <div className={'employee-detail-div'}>
                            <GroupForm id={null} selectedGroup={selectedGroup} setSelectedGroup={setSelectedGroup} handleShowDelete={handleShowDelete} categoryOptions={categoryOptions} />
                          </div>
                        </td>
                      </tr>
                    }
                    {
                      !loading && Array.isArray(groupsData) && groupsData.length > 0 && groupsData.map((group, index) => <Fragment key={index}>
                        <tr className={`cursor-pointer ${group.hash_id === selectedGroup?.hash_id ? 'selected-table-row' : 'table-row'}`} onClick={() => setSelectedGroup(group.hash_id === selectedGroup?.hash_id ? null : group)} data-testid={`row-${group?.hash_id}`}>
                          <td className="text-center v-middle"><i className={`open-row-icon bi bi-chevron-${group.hash_id === selectedGroup?.hash_id ? 'up' : 'down'}`}></i></td>
                          <td>
                            <TextTruncate text={group.category} resizedCount={resizedCount} />
                          </td>
                          <td>
                            <TextTruncate text={group.name} resizedCount={resizedCount} />
                          </td>
                          <td className="text-truncate">
                            {
                              group.evaluators_count > 0 && group.first_evaluators.data.map((ev, i) => <span key={i} className="badge badge-pill badge-evaluator mr-1" data-tooltip-id="role-tooltip" data-tooltip-content={translate(!isDbModuleActive ? 'roles.evaluator' : 'state_employee.first_evaluator')}>{ev.full_name}</span>)
                            }
                            {
                              (group.evaluators_count > 0 && group.evaluators_count > 4) && <span className="badge badge-pill badge-evaluator mr-1">+{group.evaluators_count - 4}</span>
                            }
                            {
                              isDbModuleActive ? <>
                                {
                                  group?.secondary_evaluators_count > 0 && group?.first_secondary_evaluators?.data.map((ev, i) => <span key={i} className="badge badge-pill badge-secondary-evaluator mr-1" data-tooltip-id="role-tooltip" data-tooltip-content={translate('state_employee.second_evaluator')}>{ev.full_name}</span>)
                                }
                                {
                                  (group?.secondary_evaluators_count > 0 && group?.secondary_evaluators_count > 4) && <span className="badge badge-pill badge-secondary-evaluator mr-1">+{group?.secondary_evaluators_count - 4}</span>
                                }
                              </> : <>
                                {
                                  group.reviewers_count > 0 && group.first_reviewers.data.map((ev, i) => <span key={i} className="badge badge-pill badge-reviewer mr-1" data-tooltip-id="role-tooltip" data-tooltip-content={translate('roles.reviewer')}>{ev.full_name}</span>)
                                }
                                {
                                  (group.reviewers_count > 0 && group.reviewers_count > 4) && <span className="badge badge-pill badge-reviewer mr-1">+{group.reviewers_count - 4}</span>
                                }
                              </>
                            }
                            {
                              (group.evaluators_count + ((isDbModuleActive ? group?.secondary_evaluators_count : group.reviewers_count) ?? 0) === 0) && <span className="text-75 font-weight-bold">
                                <i className="bi bi-exclamation-triangle-fill text-warning mr-1 text-smaller"></i>
                                {editableTranslate(isDbModuleActive ? 'state_employee.no_first_second_evaluator' : 'group_setting.no_evaluator_reviewer')}
                              </span>
                            }
                          </td>
                        </tr>
                        {
                          group.hash_id === selectedGroup?.hash_id && <tr className="detail-row">
                            <td colSpan={4}>
                              <div className={'employee-detail-div'}>
                                <GroupForm id={selectedGroup?.hash_id} selectedGroup={selectedGroup} setSelectedGroup={setSelectedGroup} handleShowDelete={handleShowDelete} categoryOptions={categoryOptions} />
                              </div>
                            </td>
                          </tr>
                        }
                      </Fragment>)
                    }
                    {
                      (loading || !groupsData) && <tr>
                        <td colSpan={4}>
                          <div className="d-flex justify-content-center" data-testid="loading">
                            <div className="spinner-border" role="status">
                              <span className="sr-only">Wird geladen...</span>
                            </div>
                          </div>
                        </td>
                      </tr>
                    }
                    {
                      !loading && Array.isArray(groupsData) && groupsData.length === 0 && <tr>
                        <td colSpan={4} className="text-center" data-testid="empty-state">{editableTranslate('global.no_data')}</td>
                      </tr>
                    }
                  </tbody>
                </table>
                <ReactTooltip id="role-tooltip" place="bottom" />
              </div>
              <TableScrollbar tableRef={tableRef} data={groupsData} trigger={resizedCount} />
            </div>
          </div>

          <Modal
            open={showDeleteModal}
            danger
            title={editableTranslate('group_setting.delete_group_confirmation_title', 'Gruppe löschen?')}
            cancelLabel={editableTranslate('form.no')}
            okLabel={editableTranslate('form.delete.yes')}
            onClose={handleHideDelete}
            onConfirm={handleYesDelete}
            testIdPrefix="groups-delete-"
          >
            {editableTranslate('group_setting.delete_group_confirmation')}
          </Modal>
        </div>
      </div>
    </>
  );
}
